import { AlertTriangle, Loader2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";

/**
 * Iter-61: Confirmation dialog shown before overwriting an already-
 * uploaded script file. We don't auto-delete parsed scenes — that
 * still requires an explicit re-parse step — but the upload itself
 * replaces the source PDF, which can affect scene→page jump and
 * navigation. So we make the user opt in.
 *
 * Iter-92: when `busy` is true the dialog stays open and replaces the
 * action row with an in-place spinner + "Uploading replacement
 * script…" so the user always has visible feedback while the file
 * uploads. Cancel is suppressed mid-upload to avoid orphaning a
 * half-written request.
 */
export default function ConfirmReplaceScriptDialog({
    open,
    onOpenChange,
    currentFilename,
    onConfirm,
    busy = false,
}) {
    return (
        <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
            <DialogContent
                data-testid="confirm-replace-script-dialog"
                className="max-w-md bg-card border-amber-500/30"
            >
                <DialogHeader className="pr-8">
                    <DialogTitle className="font-display text-xl font-bold text-white flex items-center gap-2">
                        <AlertTriangle size={18} className="text-amber-300" />
                        Replace Script?
                    </DialogTitle>
                    <DialogDescription className="text-neutral-300">
                        {currentFilename ? (
                            <>
                                The current script{" "}
                                <span className="text-white font-mono text-xs bg-secondary px-1.5 py-0.5 rounded">
                                    {currentFilename}
                                </span>{" "}
                                will be replaced by a new version.
                            </>
                        ) : (
                            "The current script will be replaced by a new version."
                        )}
                    </DialogDescription>
                </DialogHeader>
                <ul className="mt-2 space-y-1.5 text-[13px] text-emerald-900 dark:text-emerald-50 leading-relaxed rounded-lg border border-emerald-300 bg-emerald-50 dark:border-emerald-500/50 dark:bg-emerald-500/20 p-3">
                    <li>
                        ·{" "}
                        <span className="font-semibold">
                            Photos, notes and looks are preserved.
                        </span>
                    </li>
                    <li>
                        ·{" "}
                        <span className="font-semibold">
                            Previous versions remain recoverable
                        </span>{" "}
                        in your Scripts history.
                    </li>
                </ul>
                {busy ? (
                    <div
                        data-testid="confirm-replace-script-progress"
                        className="mt-4 flex items-center gap-3 rounded-lg border border-white/10 bg-secondary/40 p-3 text-sm text-white"
                    >
                        <Loader2 size={16} className="animate-spin text-primary shrink-0" />
                        <span>Uploading replacement script…</span>
                    </div>
                ) : (
                    <div className="flex gap-2 pt-4">
                        <Button
                            type="button"
                            data-testid="confirm-replace-script-cancel"
                            variant="secondary"
                            onClick={() => onOpenChange(false)}
                            className="flex-1 min-tap"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            data-testid="confirm-replace-script-confirm"
                            onClick={onConfirm}
                            className="flex-1 min-tap bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                        >
                            Replace Script
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
